import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { LabManagerService } from '../../../lab-manager/lab-manager.service';
import { LabUploadProblem, LabUploadResult, UploadVerdict } from '../../../lab-manager/models/lab-upload-result';
import {
  classifyUpload,
  isRejectedUpload,
  isSuccessStatus,
  orderedProblems,
  parseUploadBody,
  summariseUpload,
  uploadErrorMessage,
} from '../../../lab-manager/utils/lab-upload-response';

export interface ResultsUploadDialogData {
  submissionId: number;
  labId: number;
  typesId: number;
  /** Soft tissue and soil post to different endpoints. */
  isSoftTissue: boolean;
}

/** What the caller needs once the dialog is finished with. */
export interface ResultsUploadDialogResult {
  verdict: UploadVerdict;
  result: LabUploadResult | null;
  /** True when something was stored, so the submission can move on. */
  stored: boolean;
}

/**
 * Choosing a results file, uploading it, and showing what was wrong with it.
 *
 * One dialog rather than two. A refused file used to close the picker, show the
 * problems in a second dialog, and leave the user to dismiss that and start again
 * from the UPLOAD button. Fixing a spreadsheet and trying again is the normal case
 * here, not the exception, so the retry lives inside the dialog and it never closes
 * between attempts.
 *
 * <p>It makes the upload call itself. That is how dialogs in this app already work:
 * delete-sample, add-new-group and field-details all inject their services and call
 * them. Doing it here is what allows the loop; handing a file back to the caller and
 * waiting to be reopened is the behaviour being removed.
 *
 * <p>It does NOT change the submission's status. That stays with the page, which
 * owns the submission, and it reads the result this closes with.
 */
@Component({
  selector: 'app-results-upload-dialog',
  templateUrl: './results-upload-dialog.component.html',
  styleUrls: ['./results-upload-dialog.component.css'],
  standalone: false
})
export class ResultsUploadDialogComponent {

  /** picking a file, waiting on the server, or looking at what came back. */
  state: 'picking' | 'uploading' | 'problems' = 'picking';

  fileToUpload: File | null = null;
  @ViewChild('fileinput') fileinput: ElementRef;

  verdict: UploadVerdict = 'ok';
  summary = '';
  problems: LabUploadProblem[] = [];
  problemsOmitted = 0;

  /** The last response, carried back to the caller. */
  private lastResult: LabUploadResult | null = null;

  /** A genuine failure, as opposed to a file the server read and refused. */
  errorMessage: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ResultsUploadDialogData,
    public dialogRef: MatDialogRef<ResultsUploadDialogComponent>,
    private labManagerService: LabManagerService,
  ) {
  }

  get isBlocking(): boolean {
    return this.verdict === 'blocking';
  }

  get fileName(): string {
    return this.fileToUpload?.name ?? '';
  }

  openFileInput(): void {
    this.fileinput.nativeElement.click();
  }

  handleFileInput(files: FileList): void {
    this.fileToUpload = files.item(0);
    this.errorMessage = null;
  }

  /** Back to the picker, keeping the dialog open. This is the whole point. */
  chooseAnotherFile(): void {
    this.state = 'picking';
    this.fileToUpload = null;
    this.errorMessage = null;

    // The problems stay on this.problems until the next result replaces them, so the
    // user can still read what was wrong while choosing the corrected file.
  }

  upload(): void {
    if (!this.fileToUpload || this.state === 'uploading') {
      return;
    }

    this.state = 'uploading';
    this.errorMessage = null;

    const isCsv = this.fileToUpload.type?.includes('text/csv');
    const upload$ = this.data.isSoftTissue
      ? this.labManagerService.uploadSoftTissueFile(
          this.data.submissionId, this.data.labId, this.data.typesId, this.fileToUpload, isCsv)
      : this.labManagerService.uploadSoilFile(this.data.submissionId, this.fileToUpload, isCsv);

    upload$.subscribe({
      next: (body) => this.show(parseUploadBody(body)),
      error: (err) => {
        // A 2xx can arrive here. The service posts with no responseType, so Angular
        // defaults to json and a success whose body is not JSON makes the parser
        // throw, surfacing an HttpErrorResponse that still carries the 2xx.
        if (isSuccessStatus(err?.status)) {
          this.show(parseUploadBody(err?.error?.text ?? err?.error));
          return;
        }

        // A 422 is the file being refused, not the request failing. The body is the
        // report, which is the whole point of answering 422 rather than 200: show the
        // problems instead of a transport error.
        if (isRejectedUpload(err?.status)) {
          const report = parseUploadBody(err?.error?.text ?? err?.error);

          // A refused file whose body will not parse is still a refused file. Passing
          // null here would classify as "unknown", close the dialog and let the page
          // move the submission on as though the upload had worked.
          this.show(report ?? {
            accepted: false,
            blockingCount: 1,
            problems: [{
              severity: 'BLOCKING',
              message: 'The file was not accepted, and the reason could not be read. Nothing has been stored.'
            }]
          });
          return;
        }

        // A real failure is not a problem list: the file was never read. Back to the
        // picker with the reason, rather than an empty problems screen.
        this.state = 'picking';
        this.errorMessage = uploadErrorMessage(err);
      }
    });
  }

  private show(result: LabUploadResult | null): void {
    this.lastResult = result;
    this.verdict = classifyUpload(result);
    this.summary = summariseUpload(result, this.verdict);
    this.problems = orderedProblems(result);
    this.problemsOmitted = result?.problemsOmitted ?? 0;

    // Nothing to look at, so nothing to keep the dialog open for.
    if (this.verdict === 'ok' || this.verdict === 'unknown') {
      this.finish();
      return;
    }

    this.state = 'problems';
  }

  /** Accept what was stored and let the page carry on. */
  finish(): void {
    this.dialogRef.close({
      verdict: this.verdict,
      result: this.lastResult,
      stored: this.verdict !== 'blocking',
    } as ResultsUploadDialogResult);
  }

  /**
   * Back out without accepting the upload.
   *
   * Always closes with null, whatever the last verdict was, so the page leaves the
   * submission exactly where it was.
   *
   * This used to return stored:true after a warning, reasoning that the file had already
   * been committed server side so saying otherwise would be a lie. The effect was that
   * Close did exactly what Continue does, which is not a choice at all. Accepting a
   * warned upload is a decision, and Continue is where the user makes it.
   *
   * Closing cannot un-store what the server committed, and it does not need to: the
   * submission does not move, UPLOAD stays available from statuses 2, 5 and 8, and the
   * next upload deletes those rows before writing its own, so they are replaced rather
   * than added to.
   */
  cancel(): void {
    this.dialogRef.close(null);
  }
}
